import { BlogArticle } from '../../components/blog/BlogArticle.tsx';
import { Link } from '../../components/Link.tsx';
import type { BlogPostMeta } from './posts.ts';

export const webMcpPost: BlogPostMeta = {
  slug: 'webmcp',
  titleKey: 'blog.webmcp.title',
  summaryKey: 'blog.webmcp.summary',
  publishedOn: '2026-10-07',
};

export function WebMcp() {
  return (
    <BlogArticle post={webMcpPost}>
      <p className="mkt-eyebrow">TLDR</p>
      <ul>
        <li>Goalpost already lets AI tools and agents like Claude and ChatGPT read and edit your Goalpost documents through our MCP server.</li>
        <li>With support for WebMCP, an AI agent browsing alongside you can now edit your Goalpost document right on the page, the same way you do.</li>
      </ul>
      <h2 id="why">Your agent, on the page with you</h2>
      <p>
        Open your document in Goalpost in a browser with an AI agent built in,
        and the agent can now work on it with you. Ask it to add a note under a
        section, move something, reword a line, or undo the last change, and it
        happens on the page in front of you, exactly as if you had done it
        yourself.
      </p>
      <p>
        For a while now, you've been able to connect Claude, ChatGPT, or
        whatever AI agent harness you prefer to Goalpost's{' '}
        <Link to="/features/ai">MCP server</Link>, ask it questions about your
        documents, and have it make changes on your behalf.
      </p>
      <p>
        We're seeing chat interfaces (like Claude and ChatGPT) branch out to better interact with the web without pushing users to use special-purpose plugins or connectors. Those are powerful integrations, but you have to set them up in advance, which is friction, so they don't work well for ad hoc use cases.
      </p>
      <p>
        We're also seeing Chrome and other browsers start to bring AI tools like Gemini right into a traditional browser.
        This co-browsing, chat alongside the web, can be a more natural way to work, but a website designed for
        humans can be hard for an AI to drive from the outside.
      </p>
      <p>
        Now, Goalpost's change batch editor tells the agent in your
        browser exactly what can be done on the page. The agent's changes land the same way a click does: in the same
        editor, with the same undo history, and with you watching. You can
        switch back and forth between doing something yourself and asking the
        agent to, without copying anything between the chat and the page. No
        setup and no Pro plan, because the agent is using the page you already
        have open.
      </p>
      <p>
        This is your agent reaching Goalpost on the web. You can also bring Goalpost directly into your chat with our <Link to="/docs/ai-integration">MCP Server</Link> (also known as a Connector).
      </p>

      <h2 id="technical">Technical detail</h2>
      <p>
        <a href="https://github.com/webmachinelearning/webmcp">WebMCP</a> is a
        proposed web standard that lets a web page offer its own tools to an AI agent running in the browser. Where MCP
        connects an agent to a service, WebMCP connects it to the page you have
        open. It has no MCP server: the page itself exposes its tools through a
        well-known JavaScript object in the browser.
      </p>

      <h2 id="try">How to try it</h2>
      <p>
        Prompt the ChatGPT desktop app to open Goalpost by typing “open https://app.movegoalpost.com”.
        It opens Goalpost in its built-in browser. You sign in as usual. Open your
        document and a change you haven't submitted yet. Then, instead of
        editing it yourself, ask ChatGPT to: “add a note under Accounts that
        sessions expire after 30 days.” The change lands in the editor in
        front of you.
      </p>
      <p>
        Now that you're signed in, you should be able to prompt ChatGPT to open your actual document page directly in the future.
      </p>

      <h2 id="goalpost">New to Goalpost?</h2>
      <p>
        Goalpost is a shared place for people and AI assistants to write down
        what they are building. A document is a structured tree of sections
        and items, every edit is a versioned change that goes through approval,
        and approving is something only people do. The free plan covers
        everything except connecting an assistant. Start at{' '}
        <Link to="/docs">Getting Started</Link>, or{' '}
        <Link to="/register">sign up</Link>.
      </p>
    </BlogArticle>
  );
}
